import React from 'react';
import SEOHead from '../components/seo/SEOHead';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import { Link } from 'react-router-dom';

const AmazonApiUse = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="Amazon API Data Access Disclosure"
        description="How CustomSync uses Amazon SP-API data securely and compliantly, including order retrieval, fulfillment syncing, data minimization, and retention."
        keywords="Amazon SP-API, data access disclosure, privacy, PII, CustomSync, Direct-to-Consumer Shipping Restricted"
        canonicalUrl="https://customsync.app/amazon-api-use"
      />
      <Header />

      <main className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Amazon API Data Access Disclosure</h1>
        <p className="text-gray-600 mb-10">Last updated: October 30, 2025.</p>

        <p className="text-lg text-gray-700 mb-8">
          CustomSync uses the Amazon Selling Partner API (SP-API) under the Direct-to-Consumer Shipping (Restricted) role to enable merchants to efficiently manage custom and personalized orders.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">How CustomSync Uses Amazon Data</h2>
        <p className="text-gray-700 mb-6">
          CustomSync securely connects to a seller’s Amazon account to perform the following functions:
        </p>

        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Order Retrieval:</h3>
            <p className="text-gray-700">
              Retrieves order data, including item details, customization text, and buyer-uploaded images for Amazon Custom and Handmade orders.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Fulfillment Synchronization:</h3>
            <p className="text-gray-700">
              Syncs these customization details and shipping information directly into connected fulfillment platforms such as ShipStation or Veeqo, allowing sellers to print accurate packing slips.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Data Minimization &amp; Retention:</h3>
            <p className="text-gray-700">
              Personally Identifiable Information (PII) is accessed only as needed to complete the above functions. No PII is stored long-term; all temporary data is securely deleted within 30 days of order date.
            </p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Security &amp; Compliance</h2>
        <ul className="list-disc pl-6 space-y-3 text-gray-700">
          <li>All API communication is encrypted.</li>
          <li>Data at rest (when temporarily cached) is encrypted using AES-256 encryption.</li>
          <li>Access is limited to authenticated and authorized accounts.</li>
          <li>PII is never included in logs or shared with any third parties beyond seller-authorized systems.</li>
          <li>Sellers may revoke access at any time through their Amazon Developer Permissions page.</li>
          <li>CustomSync complies with Amazon’s Data Protection Policy, Acceptable Use Policy, and Selling Partner API Developer Agreement.</li>
        </ul>

        <p className="text-gray-700 mt-8">
          For more details, see our{' '}
          <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</Link>.
        </p>
      </main>

      <Footer />
    </div>
  );
};

export default AmazonApiUse;

